/**
 * Auth Store with Runtime Validation
 *
 * Uses Zod schemas to validate API responses at runtime instead of type assertions.
 * This ensures type safety and catches integration bugs immediately.
 *
 * Token storage threat model: see useApi.ts. Access token lives in module
 * memory (useApi.ts), refresh token in sessionStorage (transitional) until
 * the API issues httpOnly cookies. User profile is persisted to
 * localStorage for fast initial render only.
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import * as Sentry from '@sentry/vue'
import type { UserProfile, TokenPair, UserRole } from '@gencay-akademi/shared-types'
import {
  UserProfileSchema,
  TokenPairSchema,
  AuthResponseSchema,
} from '@gencay-akademi/shared-types'
import {
  useApi,
  saveTokens,
  saveRefreshToken,
  setAccessToken,
  getStoredRefreshToken,
  clearAuth as clearAuthStorage,
  validateApiData,
  validateApiResponse,
  ValidationError,
} from '@/composables/useApi'

const USER_KEY = 'auth_user'

/**
 * Best-effort error reporter. Logs to console only in dev; ships to Sentry
 * in production when the SDK has been initialized (VITE_SENTRY_DSN set).
 */
function reportError(message: string, error: unknown, context?: Record<string, unknown>): void {
  if (import.meta.env.DEV) {
    console.error(message, error, context)
  }
  // Sentry may not be initialized (no DSN); the SDK no-ops the call when so.
  try {
    if (error instanceof Error) {
      Sentry.captureException(error, context ? { extra: context } : undefined)
    } else {
      Sentry.captureMessage(message, {
        level: 'error',
        extra: { ...context, raw: error },
      })
    }
  } catch {
    // ignore — never let logging break the auth flow
  }
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<UserProfile | null>(null)
  const tokens = ref<TokenPair | null>(null)
  const initialized = ref(false)
  const loading = ref(false)

  // Getters
  const isAuthenticated = computed(() => !!tokens.value?.accessToken && !!user.value)
  const isAdmin = computed(() => user.value?.role === 'admin')
  const isTeacher = computed(() => user.value?.role === 'teacher')
  const isStudent = computed(() => user.value?.role === 'student')
  const isParent = computed(() => user.value?.role === 'parent')
  /** ADMIN or TEACHER — staff role (content management) */
  const isStaff = computed(() => user.value?.role === 'admin' || user.value?.role === 'teacher')

  /**
   * Multi-role guard helper. Used by router meta.roles and sidebar nav items.
   * Returns true when the current user's role is in the allowed list, OR when
   * the allowed list is empty/undefined (i.e., the route is role-agnostic).
   */
  function hasAnyRole(roles?: readonly UserRole[]): boolean {
    if (!roles || roles.length === 0) return true
    const r = user.value?.role
    return !!r && roles.includes(r)
  }

  // Initialize from storage with validation
  async function initialize() {
    if (initialized.value) return

    try {
      const storedRefreshToken = getStoredRefreshToken()
      const storedUser = localStorage.getItem(USER_KEY)

      if (storedRefreshToken && storedUser) {
        // Validate stored user (raw object, NOT an API envelope)
        const parsedUser = JSON.parse(storedUser)
        user.value = validateApiResponse(UserProfileSchema, parsedUser, {
          path: ['user'],
          errorMessage: 'Invalid stored user data',
        })

        // We don't have the access token (in-memory only, lost on reload)
        // — exchange the refresh token for a fresh access token.
        const { client } = useApi()
        const response = await client.refreshToken(storedRefreshToken)

        if (!response.success || !response.data) {
          throw new Error('Failed to refresh session')
        }

        const refreshed = validateApiData(TokenPairSchema, response, {
          path: ['initialize', 'refresh'],
          errorMessage: 'Invalid refreshed token structure',
        })

        tokens.value = refreshed
        saveTokens(refreshed)

        // Verify token is still valid by refreshing profile
        await refreshProfile()
      }
    } catch (error: unknown) {
      reportError('Failed to initialize auth', error)

      // Clear invalid data — duck-type validation error check
      // (ValidationError class not exported anywhere yet; treat any err with
      //  .details bag as a validation error for richer logging).
      const details = (error as { details?: unknown } | null)?.details
      if (details !== undefined) {
        reportError('Validation error during initialization', error, { details })
      }

      clearAuth()
    } finally {
      initialized.value = true
    }
  }

  // Save user to storage
  function saveUserToStorage() {
    if (user.value) {
      localStorage.setItem(USER_KEY, JSON.stringify(user.value))
    }
  }

  // Clear auth state
  function clearAuth() {
    user.value = null
    tokens.value = null
    clearAuthStorage()
  }

  // Login with runtime validation
  async function login(email: string, password: string) {
    const { client } = useApi()
    loading.value = true
    try {
      const response = await client.login({ email, password })

      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Login failed')
      }

      // Validate the complete auth response (validateApiData unwraps the API envelope)
      const validatedData = validateApiData(AuthResponseSchema, response, {
        path: ['login'],
        errorMessage: 'Invalid login response structure',
      })

      // AuthResponseSchema already validated user and tokens via nested schemas
      user.value = validatedData.user
      tokens.value = validatedData.tokens

      // Access token → memory, refresh → sessionStorage (+ best-effort cookie)
      saveTokens(tokens.value)
      saveUserToStorage()

      return { success: true }
    } catch (error: unknown) {
      // Handle validation errors specifically (duck-type)
      const detailsBag = (error as { details?: unknown } | null)?.details
      if (detailsBag !== undefined) {
        const e = error as Error
        reportError('Login validation error', e, { details: detailsBag })
        return {
          success: false,
          error: new Error(`Validation failed: ${e.message ?? 'unknown'}`),
          validationDetails: detailsBag,
        }
      }

      return { success: false, error }
    } finally {
      loading.value = false
    }
  }

  // Logout
  async function logout() {
    const { client } = useApi()
    if (tokens.value?.refreshToken) {
      try {
        await client.logout(tokens.value.refreshToken)
      } catch (error) {
        reportError('Logout error', error)
      }
    }
    clearAuth()
  }

  // Refresh profile with validation
  async function refreshProfile() {
    const { client } = useApi()
    try {
      const response = await client.getCurrentUser()

      if (!response.success || !response.data) {
        throw new Error('Failed to get user profile')
      }

      // Validate the user profile response (validateApiData unwraps the API envelope)
      user.value = validateApiData(UserProfileSchema, response, {
        path: ['profile'],
        errorMessage: 'Invalid user profile structure',
      })

      saveUserToStorage()
    } catch (error) {
      // Clear state on any error (validation or token expired)
      clearAuth()

      // Handle validation errors
      if (error instanceof ValidationError) {
        reportError('Profile validation error', error, { details: error.details })
        throw new Error(`Profile validation failed: ${error.message}`)
      }

      throw error
    }
  }

  // Refresh tokens with validation
  async function refreshTokens() {
    const { client } = useApi()
    if (!tokens.value?.refreshToken) {
      clearAuth()
      throw new Error('No refresh token')
    }

    try {
      const response = await client.refreshToken(tokens.value.refreshToken)

      if (!response.success || !response.data) {
        throw new Error('Token refresh failed')
      }

      // Validate the refreshed tokens (validateApiData unwraps the API envelope)
      tokens.value = validateApiData(TokenPairSchema, response, {
        path: ['refresh'],
        errorMessage: 'Invalid refreshed token structure',
      })

      saveTokens(tokens.value)
      return tokens.value
    } catch (error) {
      // Clear state on any error (validation or token expired)
      clearAuth()

      // Handle validation errors
      if (error instanceof ValidationError) {
        reportError('Token refresh validation error', error, { details: error.details })
        throw new Error(`Token refresh validation failed: ${error.message}`)
      }

      throw error
    }
  }

  // Get access token (for API requests)
  function getAccessToken(): string | null {
    return tokens.value?.accessToken ?? null
  }

  // Silence unused-import warning when refresh handling is bypassed in tests
  void saveRefreshToken
  void setAccessToken

  return {
    // State
    user,
    tokens,
    initialized,
    loading,

    // Getters
    isAuthenticated,
    isAdmin,
    isTeacher,
    isStudent,
    isParent,
    isStaff,
    hasAnyRole,

    // Actions
    initialize,
    login,
    logout,
    refreshProfile,
    refreshTokens,
    getAccessToken,
    clearAuth,
  }
})
